/** Robots directive written onto indexable, prerendered routes. */
export const INDEXABLE_ROBOTS =
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

/** Robots directive for auth, print, token, and not-found routes. */
export const NOINDEX_ROBOTS = "noindex, nofollow";

/**
 * Same rule as `backend/src/services/nonIndexableHost.ts`. QA serves the
 * prerendered production HTML, which opts into index,follow. Forcing the
 * meta tag here covers a crawler that executes JS if the edge strips
 * X-Robots-Tag. Production hostnames are not matched.
 */
const NON_INDEXABLE_HOST = /(?:^|\.)qa\.flogpt\.com$/i;

export const hostMustNoindex = (hostname: string | undefined): boolean =>
    NON_INDEXABLE_HOST.test((hostname || "").split(":")[0]);

const ROBOTS_META_NAMES = ["robots", "googlebot", "bingbot"] as const;

type RobotsSnapshot = { name: string; tag: HTMLMetaElement | null; content: string | null }[];

const upsertMetaName = (name: string, content: string): void => {
    if (!content || typeof document === "undefined") return;
    let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.content = content;
};

export const captureRobotsMeta = (): RobotsSnapshot =>
    ROBOTS_META_NAMES.map((name) => {
        const tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
        return { name, tag, content: tag?.content ?? null };
    });

export const restoreRobotsMeta = (snapshot: RobotsSnapshot): void => {
    for (const prev of snapshot) {
        if (prev.tag && prev.content !== null) {
            prev.tag.content = prev.content;
        } else if (!prev.tag) {
            document.querySelector(`meta[name="${prev.name}"]`)?.remove();
        }
    }
};

/** Write the same robots directive to robots, googlebot, and bingbot. */
export const robotsDirectiveForHost = (requested: string, hostname: string | undefined): string =>
    hostMustNoindex(hostname) ? NOINDEX_ROBOTS : requested;

export const applyRobotsMeta = (content: string): void => {
    if (typeof document === "undefined" || !content) return;
    const hostname = typeof location === "undefined" ? "" : location.hostname;
    const directive = robotsDirectiveForHost(content, hostname);
    for (const name of ROBOTS_META_NAMES) {
        upsertMetaName(name, directive);
    }
};
